(() => {
  if (!window.customElements || !window.HTMLElement) return;

  const Motion = window.Motion;
  const BaseElement = window.BaseElement;
  const themeConfig = (() => {
    if (window.theme && window.theme.config) {
      return window.theme.config;
    }

    return {
      motionReduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      isTouch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      rtl: document.documentElement.getAttribute('dir') === 'rtl',
    };
  })();

  const hasInView = Motion && typeof Motion.inView === 'function';
  const hasInitWhenVisible = window.theme && typeof window.theme.initWhenVisible === 'function';
  const normalizeObserverOptions = (options) => {
    if (!options || typeof options !== 'object') return {};

    if (options.rootMargin) {
      return options;
    }

    if (options.margin) {
      return {
        ...options,
        rootMargin: options.margin,
      };
    }

    return options;
  };
  const fallbackInView = (element, callback, options = {}) => {
    if (!('IntersectionObserver' in window)) {
      requestAnimationFrame(callback);
      return () => {};
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback();
          observer.disconnect();
        }
      });
    }, normalizeObserverOptions(options));

    observer.observe(element);
    return () => observer.disconnect();
  };

  const setupInView = (element, callback, options = {}) => {
    if (hasInView) {
      return Motion.inView(element, callback, options);
    }

    return fallbackInView(element, callback, options);
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class FallbackBaseElement extends HTMLElement {
    constructor() {
      super();
      this._cleanups = new Set();
    }

    registerCleanup(dispose) {
      if (typeof dispose === 'function') {
        this._cleanups.add(dispose);
      }
    }

    disconnectedCallback() {
      this._cleanups.forEach((dispose) => dispose());
      this._cleanups.clear();
    }
  }

  const BaseElementClass = BaseElement || FallbackBaseElement;

  class HighlightedText extends HTMLElement {
    constructor() {
      super();
      this._observer = null;
    }

    get splitWords() {
      return this.closest('split-words');
    }

    async init() {
      if (this.splitWords) {
        await this.splitWords.loadPromise;
      }

      this.classList.add('animated');
    }

    connectedCallback() {
      if (themeConfig.motionReduced) {
        this.classList.add('animated');
        return;
      }

      if (hasInView) {
        Motion.inView(this, this.init.bind(this));
        return;
      }

      this._observer = fallbackInView(this, () => {
        this.init();
        if (this._observer) {
          this._observer();
          this._observer = null;
        }
      }, { threshold: 0.2 });
    }
  }

  if (!window.customElements.get('highlighted-text')) {
    customElements.define('highlighted-text', HighlightedText);
  }

  class MarqueeElement extends BaseElementClass {
    constructor() {
      super();

      if (themeConfig.motionReduced) return;

      if (!themeConfig.isTouch || window.Shopify?.designMode) {
        this._inViewCleanup = setupInView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
      } else if (hasInitWhenVisible) {
        this._initWhenVisible = new window.theme.initWhenVisible(this.init.bind(this));
      } else {
        this._inViewCleanup = fallbackInView(this, this.init.bind(this), { rootMargin: '200px 0px 200px 0px' });
      }
    }

    connectedCallback() {
      if (super.connectedCallback) {
        super.connectedCallback();
      }

      if (this.initialized) {
        this.initialized = false;
        this.init();
      }
    }

    get childElement() {
      return this.firstElementChild;
    }

    get speed() {
      return this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed'), 10) : 16;
    }

    get maximum() {
      return this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum'), 10) : (this.childElementWidth > 0 ? Math.ceil(this.parentWidth / this.childElementWidth) + 2 : 0);
    }

    get direction() {
      return this.hasAttribute('data-direction') ? this.getAttribute('data-direction') : 'left';
    }

    get parallax() {
      const allowsMobileParallax = this.hasAttribute('data-parallax-mobile')
        && window.matchMedia('(max-width: 767px)').matches;

      const supportsNativeProductParallax = allowsMobileParallax
        && document.body.classList.contains('template-product')
        && window.CSS
        && typeof window.CSS.supports === 'function'
        && window.CSS.supports('animation-timeline: view()');

      if (supportsNativeProductParallax) {
        this.setAttribute('data-native-scroll-parallax', '');
        return false;
      }

      return (!themeConfig.isTouch || allowsMobileParallax) && this.hasAttribute('data-parallax')
        ? parseFloat(this.getAttribute('data-parallax'))
        : false;
    }

    get parentWidth() {
      return this.getWidth(this);
    }

    get childElementWidth() {
      return this.getWidth(this.childElement);
    }

    getWidth(element) {
      if (!element) return 0;

      const rect = element.getBoundingClientRect();
      return rect.right - rect.left;
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;

      if (this.childElementCount === 1) {
        this.childElement.classList.add('animated');

        const maximum = this.maximum;
        for (let index = 0; index < maximum; index++) {
          const clone = this.childElement.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('button, [href]').forEach((el) => {
            el.setAttribute('tabindex', '-1');
          });
          clone.querySelectorAll('.media').forEach((media) => {
            media.classList.remove('loading');
          });
          this.appendChild(clone);
        }

        this.style.setProperty('--duration', `${(33 - this.speed) * Math.min(2.5, Math.ceil(this.childElementWidth / this.parentWidth))}s`);
      }

      if (this.parallax) {
        let translate = this.parallax * 100 / (1 + this.parallax);

        if (this.direction === 'right') {
          translate *= -1;
        }

        if (themeConfig.rtl) {
          translate *= -1;
        }

        this.setupParallax(translate);
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.classList.remove('paused');
          } else {
            this.classList.add('paused');
          }
        });
      }, { rootMargin: '0px 0px 50px 0px' });

      observer.observe(this);

      if (this.registerCleanup) {
        this.registerCleanup(() => observer.disconnect());
      } else {
        this._observer = observer;
      }
    }

    setupParallax(translate) {
      let elementTop = 0;
      let elementHeight = 0;
      let viewportHeight = 0;

      const measure = () => {
        const rect = this.getBoundingClientRect();

        elementTop = rect.top + window.scrollY;
        elementHeight = rect.height;
        viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      };

      const update = () => {
        this._parallaxFrame = null;

        const range = viewportHeight + elementHeight;
        const rawProgress = (viewportHeight + window.scrollY - elementTop) / range;
        const progress = clamp(rawProgress, 0, 1);

        this.style.transform = `translate3d(${translate * (1 - progress)}%, 0, 0)`;
      };

      const scheduleUpdate = () => {
        if (this._parallaxFrame) return;
        this._parallaxFrame = requestAnimationFrame(update);
      };

      const refresh = () => {
        measure();
        scheduleUpdate();
      };

      this.style.willChange = 'transform';
      refresh();
      window.addEventListener('scroll', scheduleUpdate, { passive: true });
      window.addEventListener('resize', refresh, { passive: true });
      window.addEventListener('load', refresh, { once: true });

      const dispose = () => {
        window.removeEventListener('scroll', scheduleUpdate);
        window.removeEventListener('resize', refresh);
        window.removeEventListener('load', refresh);
        if (this._parallaxFrame) {
          cancelAnimationFrame(this._parallaxFrame);
          this._parallaxFrame = null;
        }
        this.style.removeProperty('will-change');
      };

      if (this.registerCleanup) {
        this.registerCleanup(dispose);
        return;
      }

      this._parallaxDispose = dispose;
    }

    disconnectedCallback() {
      if (super.disconnectedCallback) {
        super.disconnectedCallback();
      }

      if (this._inViewCleanup) {
        this._inViewCleanup();
        this._inViewCleanup = null;
      }

      if (this._parallaxDispose) {
        this._parallaxDispose();
        this._parallaxDispose = null;
      }

      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }

      if (this._parallaxFrame) {
        cancelAnimationFrame(this._parallaxFrame);
        this._parallaxFrame = null;
      }
    }
  }

  if (!window.customElements.get('marquee-element')) {
    customElements.define('marquee-element', MarqueeElement);
  }
})();
